import { isFederated } from '@frontend-platform/federation-runtime';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LIVE_SAMPLE_RATE,
  RUN_NAME_LIMITS,
  STEPPER_CONFIG,
} from '../components/create-run/constants';
import {
  type CreateRunFormData,
  INITIAL_FORM_DATA,
  LIVE_START_MODE,
  STEP_KEYS,
  type StepKey,
} from '../components/create-run/types';
import { getConversationUpperLimit } from '../lib/calibration-config';
import { useCalibrationConfig } from './use-calibration-config';
import { useCalibrationEnvironments } from './use-calibration-environments';

interface UseCreateRunFormResult {
  formData: CreateRunFormData;
  setField: <K extends keyof CreateRunFormData>(key: K, value: CreateRunFormData[K]) => void;
  resetForm: () => void;
  currentStepIndex: number;
  currentStepKey: StepKey;
  isLastStep: boolean;
  isLiveEnvironment: boolean;
  isStepValid: boolean;
  isNameTaken: boolean;
  nextTooltip: string;
  goToNext: () => void;
  goToPrevious: () => void;
  isCompleted: (index: number) => boolean;
  isActive: (index: number) => boolean;
}

export function useCreateRunForm(options?: {
  /** Names of the bot's existing runs, for the duplicate-name check. */
  existingRunNames?: string[];
}): UseCreateRunFormResult {
  const [formData, setFormData] = useState<CreateRunFormData>(INITIAL_FORM_DATA);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const { defaultEnvValue, getEnvOption, isLiveEnv, resolveBotRefId } =
    useCalibrationEnvironments();
  const { calibrationConfig } = useCalibrationConfig();

  const setField = useCallback(
    <K extends keyof CreateRunFormData>(key: K, value: CreateRunFormData[K]) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setCurrentStepIndex(0);
  }, []);

  // Seed the environment axis once the pipeline resolves: EP → lowest pipeline
  // env, non-EP → Staging. Only seeds while the user has made no selection, so
  // a late-arriving ladder can never clobber a manual pick.
  useEffect(() => {
    if (!defaultEnvValue) return;
    setFormData((prev) => {
      if (prev.evaluationEnvironment) return prev;
      const botRefId = resolveBotRefId(defaultEnvValue);
      return {
        ...prev,
        evaluationEnvironment: defaultEnvValue,
        historicalEnvironment: defaultEnvValue,
        evaluationManifestId: getEnvOption(defaultEnvValue)?.manifestId ?? null,
        evaluationBotRefId: botRefId,
        historicalBotRefId: botRefId,
      };
    });
  }, [defaultEnvValue, getEnvOption, resolveBotRefId]);

  // Backfill the IDs that *depend* on the selected environment once the host
  // publishes them.
  //
  // For a non-EP bot the axis itself is hardcoded (Staging/Production), so
  // `defaultEnvValue` is truthy on the very first render and the effect above
  // selects it immediately — but `resolveBotRefId` still returns null until
  // aistudio's socialConfig-derived `environments` publish lands. Its
  // "already selected" guard then means the real refs are never picked up, and
  // the run is created with a null bot reference. Re-derive them here instead,
  // keyed on the resolver, so a late publish self-corrects.
  //
  // Only the dependent IDs are touched, never the environment choice itself,
  // so this can't clobber a manual pick. A transient resolve back to null is
  // ignored for the same reason: dropping a known-good ref would be a
  // regression, not a correction.
  useEffect(() => {
    setFormData((prev) => {
      if (!prev.evaluationEnvironment) return prev;
      const evaluationBotRefId =
        resolveBotRefId(prev.evaluationEnvironment) ?? prev.evaluationBotRefId;
      const historicalBotRefId = prev.historicalEnvironment
        ? (resolveBotRefId(prev.historicalEnvironment) ?? prev.historicalBotRefId)
        : prev.historicalBotRefId;
      const evaluationManifestId =
        getEnvOption(prev.evaluationEnvironment)?.manifestId ?? prev.evaluationManifestId;
      if (
        prev.evaluationBotRefId === evaluationBotRefId &&
        prev.historicalBotRefId === historicalBotRefId &&
        prev.evaluationManifestId === evaluationManifestId
      ) {
        return prev;
      }
      return { ...prev, evaluationBotRefId, historicalBotRefId, evaluationManifestId };
    });
  }, [getEnvOption, resolveBotRefId]);

  // biome-ignore lint/style/noNonNullAssertion: STEPPER_CONFIG always has at least one entry
  const currentStepKey = (STEPPER_CONFIG[currentStepIndex] ?? STEPPER_CONFIG[0]!).key;
  const isLastStep = currentStepIndex === STEPPER_CONFIG.length - 1;
  const isLiveEnvironment = isLiveEnv(formData.evaluationEnvironment);
  // Derived here rather than passed in: `runName` lives in this hook's state,
  // so a caller would have to read it back out of the value this hook returns
  // to compute it. Matches the rename-metric modal's comparison — trimmed and
  // case-insensitive, since the backend treats those as the same name.
  const existingRunNames = options?.existingRunNames;
  const isNameTaken = useMemo(() => {
    const candidate = formData.runName?.trim().toLowerCase() ?? '';
    if (!candidate) return false;
    return (existingRunNames ?? []).some((name) => name.trim().toLowerCase() === candidate);
  }, [formData.runName, existingRunNames]);

  const isStepValid = useMemo(() => {
    switch (currentStepKey) {
      case STEP_KEYS.CRITERIA:
        return formData.selectedMetrics.some((g) => g.selectedCriteriaCount > 0);
      case STEP_KEYS.ENVIRONMENT: {
        if (!formData.historicalEnvironment) return false;
        // Under federation the host is the one that publishes the per-env
        // botRefIds, so requiring them here holds the user on this step for the
        // beat before that publish lands — rather than letting them continue
        // and send conversation-count calls with a null reference, then create
        // a run with no x-bot-ref-id. Standalone dev has no host to publish
        // them at all, so the refs stay null legitimately and gating on them
        // would wedge the wizard.
        if (!isFederated()) return true;
        return !!formData.evaluationBotRefId && !!formData.historicalBotRefId;
      }
      case STEP_KEYS.SCOPE: {
        if (isLiveEnvironment) {
          const startOk = formData.liveStartMode === LIVE_START_MODE.NOW || !!formData.startTime;
          const rate = Number(formData.sampleRate);
          return (
            startOk &&
            !!formData.endTime &&
            rate >= LIVE_SAMPLE_RATE.MIN &&
            rate <= LIVE_SAMPLE_RATE.MAX
          );
        }
        return !!(
          formData.startTime &&
          formData.endTime &&
          !formData.isLoadingConversationCount &&
          Number(formData.selectedConversationCount) >= calibrationConfig.conversationLowerLimit
        );
      }
      case STEP_KEYS.CONFIGURE: {
        const trimmed = formData.runName?.trim() ?? '';
        return (
          trimmed.length >= RUN_NAME_LIMITS.MIN &&
          trimmed.length <= RUN_NAME_LIMITS.MAX &&
          !isNameTaken
        );
      }
      default:
        return true;
    }
  }, [currentStepKey, formData, isLiveEnvironment, isNameTaken, calibrationConfig]);

  const nextTooltip = useMemo(() => {
    if (currentStepKey === STEP_KEYS.SCOPE && formData.isLoadingConversationCount) {
      return 'Loading conversations count…';
    }
    if (currentStepKey === STEP_KEYS.CONFIGURE && isNameTaken) {
      return 'That name is already taken. Choose a different name.';
    }
    return 'Complete all fields to continue';
  }, [currentStepKey, formData.isLoadingConversationCount, isNameTaken]);

  // Cost/API-call estimates the Configure step displays and the create payload
  // carries. Computed on leaving the Scope step (the point where the
  // conversation count and criteria selection are both final), exactly as
  // aistudio's CreateRun.vue does — the coefficients below are its formula
  // verbatim, so the two apps quote the same number for the same run.
  const computeEstimates = useCallback(() => {
    // Same cap the submit payload applies: the data-source environment decides
    // which ceiling applies, and for live runs that is always production.
    const conversations = Math.min(
      Number(formData.selectedConversationCount) || 0,
      getConversationUpperLimit(!isLiveEnv(formData.historicalEnvironment), calibrationConfig)
    );
    const criteria = formData.selectedMetrics.reduce(
      (sum, group) => sum + group.selectedCriteriaCount,
      0
    );
    const apiCalls = Math.round(conversations * criteria + conversations * 0.115 + (criteria + 1));
    const cost =
      conversations * criteria * 0.033 +
      conversations * 0.001 +
      criteria * 0.003 +
      0.003 +
      conversations * 0.217;
    setFormData((prev) => ({
      ...prev,
      estimatedAPICalls: apiCalls,
      estimatedCost: cost.toFixed(2),
    }));
  }, [
    formData.selectedConversationCount,
    formData.historicalEnvironment,
    formData.selectedMetrics,
    isLiveEnv,
    calibrationConfig,
  ]);

  const goToNext = useCallback(() => {
    if (currentStepKey === STEP_KEYS.SCOPE) computeEstimates();
    setCurrentStepIndex((idx) => Math.min(idx + 1, STEPPER_CONFIG.length - 1));
  }, [currentStepKey, computeEstimates]);
  const goToPrevious = useCallback(() => {
    setCurrentStepIndex((idx) => Math.max(idx - 1, 0));
  }, []);

  const isCompleted = useCallback((index: number) => currentStepIndex > index, [currentStepIndex]);
  const isActive = useCallback((index: number) => currentStepIndex === index, [currentStepIndex]);

  return {
    formData,
    setField,
    resetForm,
    currentStepIndex,
    currentStepKey,
    isLastStep,
    isLiveEnvironment,
    isStepValid,
    isNameTaken,
    nextTooltip,
    goToNext,
    goToPrevious,
    isCompleted,
    isActive,
  };
}
